import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMessageTemplates,
  type MessageTemplateCatalogEntry,
} from '../api/client';
import MessageTemplateEditor, { messageTemplateApiError } from './MessageTemplateEditor';

export default function MessageTemplatesSettings() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MessageTemplateCatalogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getMessageTemplates()
      .then((response) => {
        setItems(response.data.items);
        setError('');
      })
      .catch((requestError) => setError(messageTemplateApiError(requestError)))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => ({
    sms: items.filter((item) => item.channel === 'sms'),
    whatsapp: items.filter((item) => item.channel !== 'sms'),
  }), [items]);

  const updateEntry = (updated: MessageTemplateCatalogEntry) => {
    setItems((current) => current.map((item) => item.key === updated.key ? updated : item));
  };

  if (loading) return <p className="text-sm text-gray-500">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-red-600" role="alert">{error}</p>;

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-600">
        {t(
          'settings.messageTemplates.intro',
          'Edit tenant-specific Hebrew customer messages. Placeholders are validated by the server before saving.',
        )}
      </p>
      {(['sms', 'whatsapp'] as const).map((group) => (
        <section key={group} className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            {group === 'sms'
              ? t('settings.messageTemplates.smsGroup', 'SMS')
              : t('settings.messageTemplates.whatsappGroup', 'WhatsApp and receipt messages')}
          </h3>
          {groups[group].map((entry) => (
            <MessageTemplateEditor key={entry.key} entry={entry} onChange={updateEntry} />
          ))}
        </section>
      ))}
    </div>
  );
}
